import Sheet from '../../shared/components/Sheet';
import { useEditProtection } from '../../shared/components/EditProtection';
import React, { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import {
  listMenuItems, getMenuItem, syncMenu, updateMenuItem, recalculateCogs,
  CafeMenuItem, CogsBreakdownLine, MenuSummary,
} from './api';
import './cafe-menu.css';
import DataTable from '../../shared/components/DataTable';

const ADMIN_ROLES = ['Owner', 'Admin'];

// ─── Margin badge helper ──────────────────────────────────────────────────────
function MarginBadge({ pct }: { pct: number | null }) {
  if (pct == null) return <span className="cmenu-margin-badge cmenu-margin-none">No COGS</span>;
  const cls = pct >= 60 ? 'cmenu-margin-high' : pct >= 40 ? 'cmenu-margin-mid' : 'cmenu-margin-low';
  const icon = pct >= 60 ? '✅' : pct >= 40 ? '⚠️' : '🔴';
  return <span className={`cmenu-margin-badge ${cls}`}>{icon} {pct.toFixed(1)}%</span>;
}

// ─── COGS breakdown inside panel ──────────────────────────────────────────────
function CogsBreakdown({ breakdown, warnings }: { breakdown: CogsBreakdownLine[] | null; warnings: string[] | null }) {
  if (!breakdown || !breakdown.length) return <div className="cmenu-text-muted">No breakdown available.</div>;
  const total = breakdown.reduce((s, l) => s + (l.line_cost ?? 0), 0);
  return (
    <>
      <DataTable
        rows={breakdown}
        rowKey={(_line, i) => i}
        caption="COGS breakdown"
        empty="No breakdown available."
        columns={[
          {
            key: 'ingredient', header: 'Ingredient', priority: 'primary',
            render: (line) => (
              <>
                {line.type === 'sub_recipe' ? <em>↳ {line.name}</em> : line.name}
                {line.warning && <div className="cmenu-cogs-warn">⚠️ {line.warning}</div>}
              </>
            ),
          },
          { key: 'cost', header: 'Cost', priority: 'summary', numeric: true, render: (line) => (line.line_cost != null ? `$${line.line_cost.toFixed(4)}` : '—') },
          { key: 'qty', header: 'Qty', priority: 'summary', numeric: true, render: (line) => (line.quantity != null ? `${line.quantity} ${line.unit}` : '—') },
          { key: 'unit', header: 'Unit Price', priority: 'summary', numeric: true, render: (line) => (line.unit_price != null ? `$${line.unit_price.toFixed(4)}/${line.base_unit || line.unit}` : '—') },
        ]}
        footer={
          <tr>
            <td><strong>Total COGS</strong></td>
            <td className="num"><strong>${total.toFixed(4)}</strong></td>
            <td colSpan={2} />
          </tr>
        }
        footerCard={
          <>
            <div className="cs-record__title">Total COGS</div>
            <dl className="cs-record__fields">
              <div className="cs-record__field"><dt>Total</dt><dd className="num"><strong>${total.toFixed(4)}</strong></dd></div>
            </dl>
          </>
        }
      />
      {warnings && warnings.length > 0 && (
        <div style={{ marginTop: '0.5rem' }}>
          {warnings.map((w, i) => <div key={i} className="cmenu-cogs-warn">⚠️ {w}</div>)}
        </div>
      )}
    </>
  );
}

// ─── Detail panel (slide-in overlay) ─────────────────────────────────────────
interface DetailPanelProps {
  itemId: string;
  token: string | null;
  isAdmin: boolean;
  onClose: () => void;
  onUpdated: () => void;
}

const DetailPanel: React.FC<DetailPanelProps> = ({ itemId, token, isAdmin, onClose, onUpdated }) => {
  const [item, setItem] = useState<CafeMenuItem | null>(null);
  const [breakdown, setBreakdown] = useState<CogsBreakdownLine[] | null>(null);
  const [warnings, setWarnings] = useState<string[] | null>(null);
  const [recipes, setRecipes] = useState<Array<{ id: number; name: string }>>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [recalcLoading, setRecalcLoading] = useState(false);
  const [msg, setMsg] = useState('');

  // Editable fields
  const [description, setDescription] = useState('');
  const [sizeLabel, setSizeLabel] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [recipeId, setRecipeId] = useState<string>('');
  const [baseline, setBaseline] = useState('');
  const draft = JSON.stringify({ description, sizeLabel, displayName, recipeId });
  const dirty = !!baseline && draft !== baseline;
  useEditProtection(dirty);
  const requestClose = () => { if (!saving && (!dirty || window.confirm('Discard unsaved menu changes?'))) onClose(); };

  useEffect(() => {
    getMenuItem(token, itemId).then(d => {
      setItem(d.item);
      setBreakdown(d.cogs_breakdown);
      setWarnings(d.cogs_warnings);
      setDescription(d.item.description || '');
      setSizeLabel(d.item.size_label || '');
      setDisplayName(d.item.display_name || '');
      setRecipeId(d.item.recipe_id?.toString() || '');
      setBaseline(JSON.stringify({ description: d.item.description || '', sizeLabel: d.item.size_label || '', displayName: d.item.display_name || '', recipeId: d.item.recipe_id?.toString() || '' }));
    }).catch((e: Error) => setMsg(`Error: ${e.message}`)).finally(() => setLoading(false));

    // Load active recipes for linking
    fetch('/api/sop/recipes?status=Active', { headers: { 'X-Session-Token': token || '' } })
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d?.recipes) setRecipes(d.recipes); })
      .catch(() => {});
  }, [itemId, token]);

  const handleSave = async () => {
    if (!item) return;
    setSaving(true); setMsg('');
    try {
      await updateMenuItem(token, item.clover_item_id, {
        description: description || null,
        size_label: sizeLabel || null,
        display_name: displayName || item.name,
        recipe_id: recipeId ? parseInt(recipeId, 10) : null,
      });
      setBaseline(draft);
      setMsg('Saved ✓');
      onUpdated();
    } catch (e: any) { setMsg(`Error: ${e.message}`); }
    finally { setSaving(false); }
  };

  const handleRecalc = async () => {
    if (!item) return;
    setRecalcLoading(true); setMsg('');
    try {
      const res = await recalculateCogs(token, item.clover_item_id);
      setBreakdown(res.breakdown);
      setWarnings(res.warnings);
      setMsg(`COGS recalculated: $${res.cogs?.toFixed(4) ?? 'N/A'}`);
      onUpdated();
    } catch (e: any) { setMsg(`Error: ${e.message}`); }
    finally { setRecalcLoading(false); }
  };

  return (
    <Sheet title={item?.display_name || item?.name || 'Menu details'} onClose={requestClose} busy={saving}>
        {loading ? <div className="cmenu-loading">Loading…</div> : !item ? <p role="alert">{msg || 'Unable to load menu item. Close and retry.'}</p> : (
          <div>
            {msg && <div className={msg.startsWith('Error') ? 'cmenu-error' : 'cmenu-success'}>{msg}</div>}

            {/* Price + COGS summary */}
            <div className="cmenu-cogs-summary">
              <div>
                <div className="cmenu-section-title">Price</div>
                <strong>${item.price?.toFixed(2) ?? '—'}</strong>
              </div>
              <div>
                <div className="cmenu-section-title">COGS</div>
                <strong>{item.cogs != null ? `$${Number(item.cogs).toFixed(4)}` : '—'}</strong>
              </div>
              <div>
                <div className="cmenu-section-title">Margin</div>
                <MarginBadge pct={item.margin_pct} />
              </div>
            </div>

            {/* Category / availability */}
            <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', fontSize: '0.82rem' }}>
              {item.category && <span className="cmenu-cat-chip">{item.category}</span>}
              {item.size_label && <span className="cmenu-size-chip">{item.size_label}</span>}
              <span className={item.available ? 'cmenu-margin-badge cmenu-margin-high' : 'cmenu-margin-badge cmenu-margin-none'}>
                {item.available ? 'Available' : 'Unavailable'}
              </span>
            </div>

            <hr className="cmenu-divider" />

            {/* Editable fields (admin only) */}
            {isAdmin && (
              <>
                <div className="cmenu-field-group">
                  <label>Display Name</label>
                  <input className="cmenu-field-input" value={displayName} onChange={e => setDisplayName(e.target.value)} />
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
                  <div className="cmenu-field-group">
                    <label>Size Label</label>
                    <input className="cmenu-field-input" value={sizeLabel} onChange={e => setSizeLabel(e.target.value)} placeholder="Regular, Large…" />
                  </div>
                </div>
                <div className="cmenu-field-group">
                  <label>Description</label>
                  <textarea
                    className="cmenu-field-input cmenu-field-textarea"
                    value={description}
                    onChange={e => setDescription(e.target.value)}
                    rows={3}
                    placeholder="Optional menu description shown to staff"
                  />
                </div>
                <div className="cmenu-field-group">
                  <label>Linked Recipe (for COGS)</label>
                  <select className="cmenu-field-select" value={recipeId} onChange={e => setRecipeId(e.target.value)}>
                    <option value="">— No recipe linked —</option>
                    {recipes.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
                  </select>
                </div>
              </>
            )}

            <hr className="cmenu-divider" />

            {/* COGS breakdown */}
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                <div className="cmenu-section-title">COGS Breakdown</div>
                {isAdmin && item.recipe_id && (
                  <button className="cmenu-btn cmenu-btn-sm" onClick={handleRecalc} disabled={recalcLoading}>
                    {recalcLoading ? 'Calculating…' : '⟳ Recalculate'}
                  </button>
                )}
              </div>
              {!item.recipe_id
                ? <div className="cmenu-text-muted">Link a recipe above to enable COGS calculation.</div>
                : <CogsBreakdown breakdown={breakdown} warnings={warnings} />
              }
            </div>
          </div>
        )}

        {isAdmin && (
          <div className="cmenu-panel-actions">
            <button className="cmenu-btn" onClick={requestClose}>Cancel</button>
            <button className="cmenu-btn cmenu-btn-primary" onClick={handleSave} disabled={saving || loading}>
              {saving ? 'Saving…' : 'Save Changes'}
            </button>
          </div>
        )}
    </Sheet>
  );
};

// ─── Main Dashboard ───────────────────────────────────────────────────────────
const CafeMenuDashboard: React.FC = () => {
  const { sessionToken, user } = useAuth();
  const isAdmin = ADMIN_ROLES.includes(user?.role || '');

  const [items, setItems] = useState<CafeMenuItem[]>([]);
  const [summary, setSummary] = useState<MenuSummary | null>(null);
  const [lastSynced, setLastSynced] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [syncMsg, setSyncMsg] = useState('');
  const [syncing, setSyncing] = useState(false);

  // Filters
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [recipeFilter, setRecipeFilter] = useState('');
  const [availFilter, setAvailFilter] = useState('true');

  // Selected item for detail panel
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const load = useCallback((silent = false) => {
    if (!silent) setLoading(true);
    listMenuItems(sessionToken, {
      search: search || undefined,
      category: categoryFilter || undefined,
      has_recipe: recipeFilter === '' ? undefined : recipeFilter === 'yes',
      available: availFilter === '' ? undefined : availFilter === 'true',
      include_hidden: isAdmin,
    })
      .then(d => {
        setItems(d.items);
        setSummary(d.summary);
        setLastSynced(d.last_synced_at);
      })
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [sessionToken, search, categoryFilter, recipeFilter, availFilter, isAdmin]);

  useEffect(() => { load(); }, [load]);

  const handleSync = async () => {
    setSyncing(true); setSyncMsg('');
    try {
      const r = await syncMenu(sessionToken);
      setSyncMsg(`✓ Synced ${r.total} items (${r.inserted} new, ${r.updated} updated, ${r.cogs_recalculated} COGS recalculated)`);
      load(true);
    } catch (e: any) { setSyncMsg(`Error: ${e.message}`); }
    finally { setSyncing(false); }
  };

  // Derive unique categories for filter dropdown
  const categories = Array.from(new Set(items.flatMap(i => (i.category || '').split(',').map(c => c.trim())).filter(Boolean))).sort();

  const syncAgeHours = lastSynced ? (Date.now() - new Date(lastSynced).getTime()) / 3_600_000 : null;
  const syncStale = syncAgeHours != null && syncAgeHours > 25;
  const syncLabel = lastSynced
    ? syncAgeHours! < 1 ? 'Synced < 1h ago' : `Synced ${Math.round(syncAgeHours!)}h ago`
    : 'Never synced';

  const handleSearch = (e: React.FormEvent) => { e.preventDefault(); load(); };

  return (
    <div className="cmenu-page">
      <div className="cmenu-header">
        <h1>🍴 Cafe Menu</h1>
        <div className="cmenu-header-actions">
          <span className={`cmenu-sync-badge ${syncStale ? 'stale' : ''}`}>{syncLabel}</span>
          {isAdmin && (
            <button className="cmenu-btn cmenu-btn-primary" onClick={handleSync} disabled={syncing}>
              {syncing ? '⟳ Syncing…' : '⟳ Sync from Clover'}
            </button>
          )}
        </div>
      </div>

      {error && <div className="cmenu-error">{error}</div>}
      {syncMsg && <div className={syncMsg.startsWith('Error') ? 'cmenu-error' : 'cmenu-success'}>{syncMsg}</div>}

      {/* Summary cards */}
      {summary && (
        <div className="cmenu-summary">
          <div className="cmenu-stat-card">
            <div className="cmenu-stat-val">{summary.total}</div>
            <div className="cmenu-stat-lbl">Total Items</div>
          </div>
          <div className="cmenu-stat-card">
            <div className="cmenu-stat-val">{summary.linked}</div>
            <div className="cmenu-stat-lbl">Recipes Linked</div>
          </div>
          <div className="cmenu-stat-card">
            <div className="cmenu-stat-val">{summary.with_cogs}</div>
            <div className="cmenu-stat-lbl">With COGS</div>
          </div>
          <div className="cmenu-stat-card">
            <div className="cmenu-stat-val">
              {summary.avg_margin_pct != null ? `${summary.avg_margin_pct}%` : '—'}
            </div>
            <div className="cmenu-stat-lbl">Avg Margin</div>
          </div>
          <div className="cmenu-stat-card">
            <div className="cmenu-stat-val" style={{ color: '#f59e0b' }}>{summary.unlinked}</div>
            <div className="cmenu-stat-lbl">No Recipe</div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="cmenu-filters">
        <form onSubmit={handleSearch} style={{ display: 'flex', gap: '0.5rem', flex: 1 }}>
          <input
            className="cmenu-input cmenu-input-search"
            placeholder="Search items…"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
          <button type="submit" className="cmenu-btn">Search</button>
          {search && <button type="button" className="cmenu-btn" onClick={() => { setSearch(''); }}>✕</button>}
        </form>

        <select className="cmenu-select" value={categoryFilter} onChange={e => setCategoryFilter(e.target.value)}>
          <option value="">All Categories</option>
          {categories.map(c => <option key={c} value={c}>{c}</option>)}
        </select>

        <select className="cmenu-select" value={availFilter} onChange={e => setAvailFilter(e.target.value)}>
          <option value="">All</option>
          <option value="true">Available</option>
          <option value="false">Unavailable</option>
        </select>

        {isAdmin && (
          <select className="cmenu-select" value={recipeFilter} onChange={e => setRecipeFilter(e.target.value)}>
            <option value="">All items</option>
            <option value="yes">Recipe linked</option>
            <option value="no">No recipe</option>
          </select>
        )}
      </div>

      {/* Table */}
      {loading ? (
        <div className="cmenu-loading">Loading menu…</div>
      ) : (
        <div className="cmenu-table-wrap">
          <DataTable
            rows={items}
            rowKey={(item) => item.clover_item_id}
            caption="Menu items"
            moreLabel="Costing"
            empty={`No items found. ${isAdmin && !lastSynced ? 'Click "Sync from Clover" to import your menu.' : ''}`}
            rowClass={(item) => (!item.available ? 'unavailable' : undefined)}
            columns={[
              {
                key: 'item', header: 'Item', priority: 'primary',
                render: (item) => (
                  <button className="cmenu-link-btn" onClick={() => setSelectedId(item.clover_item_id)}>
                    <span style={{ fontWeight: 500 }}>{item.display_name || item.name}</span>
                    {item.description && <span className="cmenu-text-muted"> — {item.description}</span>}
                  </button>
                ),
              },
              { key: 'price', header: 'Price', priority: 'summary', numeric: true, render: (item) => (item.price != null ? `$${Number(item.price).toFixed(2)}` : '—') },
              {
                key: 'status', header: 'Status', priority: 'summary',
                render: (item) => (
                  <span className={item.available ? 'cmenu-avail' : 'cmenu-unavail'}>
                    {item.available ? '● Available' : '○ Unavailable'}
                  </span>
                ),
              },
              {
                key: 'category', header: 'Category', priority: 'summary',
                render: (item) => (item.category
                  ? item.category.split(',').map((c: string, i: number) => (
                      <span key={i} className="cmenu-cat-chip">{c.trim()}</span>
                    ))
                  : <span className="cmenu-text-muted">—</span>),
              },
              { key: 'size', header: 'Size', priority: 'summary', render: (item) => (item.size_label ? <span className="cmenu-size-chip">{item.size_label}</span> : <span className="cmenu-text-muted">—</span>) },
              ...(isAdmin ? [
                { key: 'cogs', header: 'COGS', priority: 'detail' as const, numeric: true, render: (item: any) => (item.cogs != null ? `$${Number(item.cogs).toFixed(4)}` : '—') },
                { key: 'margin', header: 'Margin', priority: 'detail' as const, render: (item: any) => <MarginBadge pct={item.margin_pct} /> },
                {
                  key: 'recipe', header: 'Recipe', priority: 'detail' as const,
                  render: (item: any) => (item.recipe_name
                    ? <span className="cmenu-recipe-link">🔗 {item.recipe_name}</span>
                    : <button className="cmenu-link-btn" onClick={e => { e.stopPropagation(); setSelectedId(item.clover_item_id); }}>+ Link Recipe</button>),
                },
              ] : []),
            ]}
          />
        </div>
      )}

      {/* Detail panel */}
      {selectedId && (
        <DetailPanel
          itemId={selectedId}
          token={sessionToken}
          isAdmin={isAdmin}
          onClose={() => setSelectedId(null)}
          onUpdated={() => { load(true); }}
        />
      )}
    </div>
  );
};

export default CafeMenuDashboard;
